import React, { useMemo, useState } from 'react';
import { isBefore, isSameDay, startOfDay } from 'date-fns';
import { Lead, Category } from '../../types';
import { useI18n } from '../../context/I18nContext';
import FollowUpList from './FollowUpList';
import Card, { CardHeader } from '../ui/Card';
import type { LeadMenuActions } from '../../utils/leadActions';

export type FollowUpTab = 'all' | 'today' | 'overdue';

interface FollowUpSectionProps {
  leads: Lead[];
  categories: Category[];
  leadMenuActions?: LeadMenuActions;
  projects?: any[];
  onEdit: (lead: Lead) => void;
  onDelete: (leadId: string) => void;
  onSeeOthers: (tab: FollowUpTab) => void;
  onLeadClick?: (lead: Lead) => void;
}

const FollowUpSection: React.FC<FollowUpSectionProps> = ({
  leads,
  categories,
  leadMenuActions,
  projects = [],
  onEdit,
  onDelete,
  onSeeOthers,
  onLeadClick,
}) => {
  const { t } = useI18n();
  const today = startOfDay(new Date());

  const todayLeads = useMemo(
    () =>
      leads
        .filter((lead) => {
          if (!lead.nextActionAt) return false;
          const followUpDate = startOfDay(new Date(lead.nextActionAt as any));
          return isSameDay(followUpDate, today);
        })
        .sort(
          (a, b) =>
            new Date(a.nextActionAt!).getTime() - new Date(b.nextActionAt!).getTime(),
        ),
    [leads, today],
  );

  const overdueLeads = useMemo(
    () =>
      leads
        .filter((lead) => {
          if (!lead.nextActionAt) return false;
          const followUpDate = startOfDay(new Date(lead.nextActionAt as any));
          return isBefore(followUpDate, today);
        })
        .sort(
          (a, b) =>
            new Date(a.nextActionAt!).getTime() - new Date(b.nextActionAt!).getTime(),
        ),
    [leads, today],
  );

  const allLeads = useMemo(
    () => [...overdueLeads, ...todayLeads],
    [overdueLeads, todayLeads],
  );

  const [activeTab, setActiveTab] = useState<FollowUpTab>('all');

  if (allLeads.length === 0) {
    return null;
  }

  const tabs: Array<{ key: FollowUpTab; label: string; count: number }> = [
    { key: 'all', label: t('followups.all'), count: allLeads.length },
    { key: 'today', label: t('followups.today'), count: todayLeads.length },
    { key: 'overdue', label: t('followups.overdue'), count: overdueLeads.length },
  ];

  const activeLeads =
    activeTab === 'all' ? allLeads : activeTab === 'today' ? todayLeads : overdueLeads;

  return (
    <Card id="followups" className="h-full xl:!pb-4">
      <CardHeader className="mb-1.5 shrink-0 border-b-0 pb-0 xl:mb-2">
        <div className="flex w-full min-w-0 gap-1 border-b border-gray-200 sm:gap-5" role="tablist" aria-label={t('followups.title')}>
          {tabs.map((tab) => {
            const selected = activeTab === tab.key;
            const countLabel = tab.count > 99 ? '99+' : String(tab.count);
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setActiveTab(tab.key)}
                className={`-mb-px flex min-w-0 flex-1 items-center justify-center gap-1 border-b-2 pb-1.5 text-sm font-medium transition-colors sm:flex-none sm:justify-start sm:gap-1.5 sm:pb-2 ${
                  selected
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <span className="truncate">{tab.label}</span>
                <span
                  className={`inline-flex shrink-0 items-center justify-center rounded-full px-1.5 py-0.5 tabular-nums text-xs font-semibold ${
                    selected
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {countLabel}
                </span>
              </button>
            );
          })}
        </div>
      </CardHeader>

      <div className="flex min-h-0 flex-1 flex-col">
        <FollowUpList
        leads={activeLeads}
        tab={activeTab}
        categories={categories}
        leadMenuActions={leadMenuActions}
        projects={projects}
        onEdit={onEdit}
        onDelete={onDelete}
        onSeeOthers={() => onSeeOthers(activeTab)}
        onLeadClick={onLeadClick}
      />
        <div className="min-h-0 flex-1" aria-hidden />
      </div>
    </Card>
  );
};

export default FollowUpSection;
